import { useId } from 'react';

/**
 * Der Kursverlauf als Linie auf der Karte.
 *
 * Die Arbeitsweise beginnt mit der Trendbeurteilung — erst danach wird eine
 * Stop-Loss-Linie gesetzt. Steht der Verlauf schon in der Liste, ist ohne einen
 * einzigen Klick erkennbar, welcher Wert überhaupt Aufmerksamkeit braucht.
 *
 * Bewusst eine Linie und kein Kerzendiagramm: Auf knapp 50 Pixeln Höhe wären
 * Kerzen nicht mehr auflösbar. Wer Hoch und Tief braucht, öffnet den Chart.
 */

interface PriceSparklineProps {
  /** Schlusskurse, ältester zuerst. */
  closes: readonly number[];
  /** Der Stop als gestrichelte Linie; `null`, solange keiner gesetzt ist. */
  stopPrice: number | null;
  label: string;
}

/* Das Koordinatensystem der Zeichnung. Die Breite skaliert über `viewBox` mit
 * der Karte; die Zahlen sind daher nur Verhältnisse, keine Pixel. */
const WIDTH = 320;
const HEIGHT = 54;
/* Luft am oberen und unteren Rand, damit die Linie den Rahmen nicht berührt. */
const INSET = 3;

export const PriceSparkline = ({
  closes,
  stopPrice,
  label,
}: PriceSparklineProps): React.JSX.Element | null => {
  /*
   * Je Karte ein eigener Verlauf, sonst griffen alle Karten auf denselben zu und
   * trügen die Farbe der ersten.
   *
   * Ohne Doppelpunkte: `useId` liefert `:r0:`, und ein Doppelpunkt in einem
   * `url(#…)`-Verweis ist zwar zulässig, wird aber nicht von jedem Renderer
   * gleich behandelt. Der Fehlerfall wäre eine Fläche ohne Füllung — sichtbar
   * nur, wenn man weiß, dass dort etwas fehlt.
   */
  const gradientId = `verlauf-${useId().replace(/:/g, '')}`;

  /* Für eine Linie braucht es zwei Punkte. Ein einzelner Kurs ergibt keinen
   * Verlauf, und die Skalierung teilte durch null. */
  if (closes.length < 2) return null;

  /*
   * Der Stop gehört in den Wertebereich, auch wenn er weit unter dem tiefsten
   * Kurs liegt. Eine Linie außerhalb des Bildes verschwiege genau die
   * Information, um die es geht — und die entstehende Stauchung der Kurslinie
   * ist keine Verzerrung, sondern die ehrliche Aussage, dass viel Luft bleibt.
   */
  const low = Math.min(...closes, ...(stopPrice === null ? [] : [stopPrice]));
  const high = Math.max(...closes, ...(stopPrice === null ? [] : [stopPrice]));
  const span = high - low || 1;

  const x = (index: number): number => (index / (closes.length - 1)) * WIDTH;
  const y = (price: number): number =>
    HEIGHT - INSET - ((price - low) / span) * (HEIGHT - INSET * 2);

  const line = closes
    .map(
      (close, index) => `${index === 0 ? 'M' : 'L'}${x(index).toFixed(1)},${y(close).toFixed(1)}`,
    )
    .join(' ');
  const area = `${line} L${WIDTH},${HEIGHT} L0,${HEIGHT} Z`;

  /*
   * Die Farbe beschreibt, was die Linie zeigt: wo sie endet gegenüber ihrem
   * Anfang.
   *
   * Zuvor folgte sie dem Tagesergebnis. Das hatte zwei Nachteile: Es stand bereits
   * farbig neben dem Kurs, und es ließ eine Linie über ein halbes Jahr nach den
   * letzten 24 Stunden aussehen. Bei Rheinmetall wechselte sie deswegen von rot
   * auf grün, obwohl der Wert über 120 Tage 28 Prozent verloren hat.
   *
   * Die Zeichnung bestimmt das aus ihren eigenen Daten, statt es gereicht zu
   * bekommen: Sie ist die einzige Stelle, die den gezeigten Zeitraum kennt.
   */
  const erster = closes[0]!;
  const lastClose = closes.at(-1)!;
  const stroke = lastClose < erster ? 'var(--danger)' : 'var(--ok)';

  return (
    <svg
      className="sparkline"
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={label}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity="0.22" />
          <stop offset="100%" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>

      <path d={area} fill={`url(#${gradientId})`} stroke="none" />
      {/*
        `non-scaling-stroke`: Die Zeichnung wird über `preserveAspectRatio="none"`
        auf die Kartenbreite gezogen. Ohne diese Angabe würde die Strichstärke
        mitgezerrt und die Linie je nach Spaltenbreite unterschiedlich dick.
      */}
      <path
        d={line}
        fill="none"
        stroke={stroke}
        strokeWidth="1.6"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />

      {stopPrice === null ? null : (
        <line
          x1="0"
          y1={y(stopPrice).toFixed(1)}
          x2={WIDTH}
          y2={y(stopPrice).toFixed(1)}
          stroke="var(--danger)"
          strokeWidth="1"
          strokeDasharray="4 3"
          vectorEffect="non-scaling-stroke"
        />
      )}

      {/* Der heutige Schluss als Punkt: Das Ende der Linie ist die Gegenwart. */}
      <circle cx={WIDTH} cy={y(lastClose).toFixed(1)} r="2.6" fill={stroke} />
    </svg>
  );
};
